import { Button as BaseButton } from "../ui/button";
import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";

type ButtonMode = "primary" | "secondary" | "ghost" | "destructive";
type ButtonSize = "default" | "sm" | "lg" | "icon";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  mode?: ButtonMode;
  size?: ButtonSize;
  onClick?: () => void;
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    mode = "primary",
    size = "default",
    onClick,
    className = "",
    disabled = false,
    asChild = false,
    ...props
  },
  ref,
) {
  const getButtonStyles = () => {
    switch (mode) {
      case "primary":
        return "bg-brand hover:bg-brand/90 text-primary-foreground font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed";
      case "secondary":
        return "border-orange-500 text-orange-500 hover:bg-orange-50 hover:border-orange-600 hover:text-orange-600 disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed";
      case "ghost":
        return "hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50";
      case "destructive":
        return "font-semibold disabled:opacity-50";
      default:
        return "bg-brand hover:bg-brand/90 text-primary-foreground font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed";
    }
  };

  const getVariant = () => {
    switch (mode) {
      case "secondary":
        return "outline";
      case "ghost":
        return "ghost";
      case "destructive":
        return "destructive";
      default:
        return undefined;
    }
  };

  return (
    <BaseButton
      ref={ref}
      variant={getVariant()}
      size={size}
      className={`${getButtonStyles()} ${className}`}
      onClick={onClick}
      disabled={disabled}
      asChild={asChild}
      {...props}
    >
      {children}
    </BaseButton>
  );
});

export default Button;
